import * as React from 'react';
import { Alert, Box, TextField, Typography } from '@mui/material';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import {
  KlaraBrief,
  KlaraConstants,
  KlaraDocument,
  KlaraProject,
  KlaraVariables,
} from '../../model/klara';
import { useAppTranslation } from '../../services/i18n';
import { EvaluationBand } from './components/EvaluationBand';
import { StringListField, StructuredList } from './components/StructuredList';
import { ProcessLog } from './components/ProcessLog';
import { StatusHeader } from './components/StatusHeader';
import { SectionCard } from './components/SectionCard';
import { ChoiceCard } from './components/ChoiceCard';
import { RejectedFile, RejectedSources } from './components/RejectedSources';
import { newestProtocol } from './protocols';
import { DerivedField } from './components/DerivedField';
import { KlaraLogEntry } from '../../model/klara';

type Translate = (key: string, options?: any) => string;

type Props = {
  project: KlaraProject;
  log: KlaraLogEntry[];
  protocolUrl: (document: KlaraDocument) => string;
  /**
   * Held by the page, not by this tab, for the same reason as the inputs on
   * Vstupy — and for one more: `PageTabs` renders only the active tab, so this
   * component unmounts the moment the user looks at Vstupy. State kept here
   * would silently reset the research choice and drop the user's edits between
   * the moment they make them and the moment the topbar sends them.
   */
  edits: KlaraEdits;
  onEditsChange: (edits: KlaraEdits) => void;
  /** Paths of the fields the user has edited, which lose Klara's tint. */
  touchedFields: string[];
  onTouchField: (path: string) => void;
};

export type KlaraEdits = {
  research: boolean;
  /**
   * Only set once the user has actually revised something here: its absence
   * tells the partner that the version from message 2 stands.
   */
  brief: KlaraBrief | null;
};

/**
 * Zadání kurzu — the approval gate.
 *
 * The whole partner brief is held in state as objects and sent back whole, so
 * keys neither side documents survive: the partner requires unlisted fields
 * returned verbatim, and `variables.shared` in particular is an explicit
 * passthrough blob.
 */
export function BriefTab({
  project,
  log,
  protocolUrl,
  edits,
  onEditsChange,
  touchedFields,
  onTouchField,
}: Props) {
  const t = useAppTranslation();
  // What the user is editing, falling back to what Klara sent until they touch
  // it. No local copy: it would not survive a tab switch.
  const brief = edits.brief ?? project.brief ?? {};
  const research = edits.research;

  const setBrief = (next: KlaraBrief) => onEditsChange({ ...edits, brief: next });
  const setResearch = (value: boolean) =>
    onEditsChange({ ...edits, research: value });

  const constants = brief.constants ?? {};
  const variables = brief.variables ?? {};
  const evaluated = !!project.evaluation;
  // Read-only once the brief is away: generation runs against the version we
  // sent, so an edit here would change nothing but what the screen says.
  const editable = project.status === 'evaluation_ready';
  const protocol = newestProtocol(project.documents);

  // `path` names the edited field where one key holds several of them, as
  // learner_profile does.
  const setConstants = (patch: Partial<KlaraConstants>, path?: string) => {
    onTouchField(path ?? `constants.${Object.keys(patch)[0]}`);
    setBrief({
      ...brief,
      constants: { ...(brief.constants ?? {}), ...patch },
    });
  };

  const setVariables = (patch: Partial<KlaraVariables>) => {
    onTouchField(`variables.${Object.keys(patch)[0]}`);
    setBrief({
      ...brief,
      variables: { ...(brief.variables ?? {}), ...patch },
    });
  };

  const drafted = (path: string) => !touchedFields.includes(path);

  // Anything before the scores exist is a wait, not an empty editor. Derived
  // from the evaluation rather than a status list because the interesting
  // moments are asynchronous: the click answers 202 while the job is still a
  // draft, and brief_ready lasts seconds when message 2 follows automatically.
  const waiting =
    !project.evaluation &&
    !['completed', 'failed'].includes(project.status);

  if (waiting) {
    return (
      <Box sx={{ padding: '24px', maxWidth: '760px', margin: '0 auto' }}>
        <StatusHeader
          variant="progress"
          // The clipboard the design puts inside the ring on this screen.
          icon={<AssignmentTurnedInOutlinedIcon sx={{ fontSize: '40px' }} />}
          title={t('klara.brief.preparing')}
          body={
            // Without sources there is nothing to evaluate, so the sentence
            // about weighing them would be a lie.
            (project.sources ?? []).length > 0
              ? t('klara.brief.preparingHint')
              : t('klara.brief.preparingHintNoSources')
          }
        />
        <ProcessLog
          entries={log}
          active={!project.stalled}
          stalled={project.stalled}
          // The design nests this inside the process card. During the wait the
          // only files we can name are the ones that never uploaded — the
          // partner's per-file verdicts arrive with the evaluation.
          footer={
            <RejectedSources
              files={failedUploads(project, t)}
              total={(project.sources ?? []).length}
            />
          }
        />
        {/* The mail is sent when the evaluation completes. Before it is
            requested we cannot tell whether it will be — the API does not
            expose auto_evaluate — and a bare "Nechat doplnit Klarou" never
            gets there. */}
        {project.status === 'evaluation_requested' && (
          <Alert severity="info" sx={{ marginTop: '16px' }}>
            {t('klara.brief.mailPromise')}
          </Alert>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ padding: '24px', maxWidth: '1100px', margin: '0 auto' }}>
      <Box sx={{ textAlign: 'center', marginBottom: '24px' }}>
        {/* 24 / 13.5, as the frame sets them — not the 30px the waiting screen
            uses, which is a different heading in a different place. */}
        <Typography
          sx={{ fontSize: '24px', fontWeight: 700, color: 'var(--color-black)' }}
        >
          {t('klara.brief.heading')}
        </Typography>
        <Typography
          sx={{
            fontSize: '13.5px',
            lineHeight: '21.6px',
            color: 'var(--color-black-text)',
            marginTop: '7px',
          }}
        >
          {t('klara.brief.subheading')}
        </Typography>
      </Box>

      <EvaluationBand
        evaluation={project.evaluation}
        protocol={protocol}
        protocolUrl={protocol ? protocolUrl(protocol) : undefined}
      />

      {/* After the evaluation, from the partner's own per-file verdicts. There
          is no process card left to nest it in by then. */}
      <Box sx={{ marginBottom: '20px' }}>
        <RejectedSources
          files={unreadableFiles(project, t)}
          total={(project.evaluation?.files ?? []).length}
        />
      </Box>

      {/* Only while the choice still does something: it lives in this screen's
          state and the API never returns it, so a locked card would show a
          default rather than what was sent. */}
      {evaluated && editable && (
        <ResearchChoice
          value={research}
          onChange={setResearch}
          hasSources={(project.sources ?? []).length > 0}
        />
      )}

      <SectionCard title={t('klara.brief.audience')}>
      {/* learner_profile is two arrays in the contract, so it is two fields
          here rather than one prose box with nothing to bind to — decided
          against the design, see §4.3 of the Figma review. */}
      <DerivedField
        label={t('klara.brief.priorKnowledge')}
        caption={t('klara.brief.derivedFromTopicsAndSources')}
        drafted={drafted('constants.learner_profile.prior_knowledge')}
      >
        <StringListField
          value={constants.learner_profile?.prior_knowledge}
          onChange={(value) =>
            setConstants(
              {
                learner_profile: { ...(constants.learner_profile ?? {}), prior_knowledge: value },
              },
              'constants.learner_profile.prior_knowledge'
            )
          }
          readOnly={!editable}
        />
      </DerivedField>
      <DerivedField
        label={t('klara.brief.knowledgeGaps')}
        caption={t('klara.brief.derivedFromTopicsAndSources')}
        drafted={drafted('constants.learner_profile.knowledge_gaps')}
      >
        <StringListField
          value={constants.learner_profile?.knowledge_gaps}
          onChange={(value) =>
            setConstants(
              {
                learner_profile: { ...(constants.learner_profile ?? {}), knowledge_gaps: value },
              },
              'constants.learner_profile.knowledge_gaps'
            )
          }
          readOnly={!editable}
        />
      </DerivedField>
      <Box sx={{ display: 'flex', gap: '16px' }}>
        <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
          <DerivedField
            label={t('klara.brief.entryEducation')}
            caption={t('klara.brief.derivedFromTopics')}
            drafted={drafted('constants.entry_education_level')}
          >
            <TextField
              variant="standard"
              fullWidth
              multiline
              disabled={!editable}
              value={constants.entry_education_level ?? ''}
              onChange={(e) => setConstants({ entry_education_level: e.target.value })}
            />
          </DerivedField>
        </Box>
        <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
          <DerivedField
            label={t('klara.brief.communicationStyle')}
            caption={t('klara.brief.derivedFromTopics')}
            drafted={drafted('constants.communication_style')}
          >
            <TextField
              variant="standard"
              fullWidth
              multiline
              disabled={!editable}
              value={constants.communication_style ?? ''}
              onChange={(e) => setConstants({ communication_style: e.target.value })}
            />
          </DerivedField>
        </Box>
      </Box>
      </SectionCard>

      <SectionCard title={t('klara.brief.content')}>
      <DerivedField
        label={t('klara.brief.courseDescription')}
        caption={t('klara.brief.derivedFromTopics')}
        drafted={drafted('variables.course_description')}
      >
        <TextField
          variant="standard"
          fullWidth
          multiline
          minRows={3}
          disabled={!editable}
          value={variables.course_description ?? ''}
          onChange={(e) => setVariables({ course_description: e.target.value })}
        />
      </DerivedField>

      {/* depth, note and derived are never rendered but must survive. */}
      <DerivedField
        label={t('klara.brief.requiredContent')}
        caption={t('klara.brief.derivedFromTopicsAndSources')}
        drafted={drafted('constants.required_content')}
      >
        <StructuredList
          hint={t('klara.brief.requiredContentHint')}
          items={constants.required_content ?? []}
          fields={[{ key: 'topic' }]}
          newItem={() => ({ topic: '', depth: 'overview', derived: false })}
          onChange={(items) => setConstants({ required_content: items })}
          readOnly={!editable}
        />
      </DerivedField>

      <DerivedField
        label={t('klara.brief.terminology')}
        caption={t('klara.brief.derivedFromTopicsAndSources')}
        drafted={drafted('variables.terminology')}
      >
        <StructuredList
          items={variables.terminology ?? []}
          fields={[
            { key: 'term', label: t('klara.brief.term'), width: '30%' },
            { key: 'definition', label: t('klara.brief.definition') },
          ]}
          newItem={() => ({ term: '', definition: '' })}
          onChange={(items) => setVariables({ terminology: items })}
          readOnly={!editable}
        />
      </DerivedField>
      </SectionCard>

      <SectionCard title={t('klara.brief.constraints')}>
      <DerivedField
        label={t('klara.brief.specificRequirements')}
        caption={t('klara.brief.derivedFromTopics')}
        drafted={drafted('constants.specific_requirements')}
      >
        <StringListField
          value={constants.specific_requirements}
          onChange={(value) => setConstants({ specific_requirements: value })}
          readOnly={!editable}
        />
      </DerivedField>
      {/* question_areas carry a nested questions[] the design never shows. */}
      <DerivedField
        label={t('klara.brief.questionAreas')}
        caption={t('klara.brief.derivedFromTopicsAndSources')}
        drafted={drafted('variables.question_areas')}
      >
        <StructuredList
          items={variables.question_areas ?? []}
          fields={[{ key: 'area' }]}
          newItem={() => ({ area: '', questions: [] })}
          onChange={(items) => setVariables({ question_areas: items })}
          readOnly={!editable}
        />
      </DerivedField>
      </SectionCard>
    </Box>
  );
}

/**
 * Files that never reached the partner — known from our own upload.
 *
 * `t` is passed in rather than pulled from the hook: these run on one branch of
 * the render each, and a hook cannot live on a branch.
 */
function failedUploads(project: KlaraProject, t: Translate): RejectedFile[] {
  return (project.sources ?? [])
    .filter((source) => source.upload_status === 'failed')
    .map((source) => ({
      name: source.file_name,
      reason: t('klara.evaluation.fileStatus.upload_failed'),
    }));
}

/**
 * Files the partner could not read. Their scores are shown per file on Vstupy;
 * this block is only about the ones that never made it in.
 */
function unreadableFiles(project: KlaraProject, t: Translate): RejectedFile[] {
  return (project.evaluation?.files ?? [])
    .filter((file) => file.status && file.status !== 'success')
    .map((file) => ({
      name: file.filename ?? '',
      // `file_corrupted` and `download_failed` are their words; an unfamiliar
      // one still renders, the same way an unknown log event does.
      reason: t(`klara.evaluation.fileStatus.${file.status}`, {
        defaultValue: '',
      }),
    }));
}

/**
 * Research is the only in-flow remedy for thin sources, which is why it is
 * pre-selected and why the choice disappears when there are no sources at all
 * — with nothing of the customer's to keep, there is nothing to choose between.
 */
function ResearchChoice({
  value,
  onChange,
  hasSources,
}: {
  value: boolean;
  onChange: (value: boolean) => void;
  hasSources: boolean;
}) {
  const t = useAppTranslation();

  // The S2/S4 DevNote: with no sources there is nothing to choose between, so
  // the cards give way to the single line that says what will happen.
  if (!hasSources) {
    return (
      <SectionCard title={t('klara.brief.researchTitle')}>
        <Typography sx={{ fontSize: '14px', color: 'var(--color-black-text)' }}>
          {t('klara.brief.researchOnlyOption')}
        </Typography>
      </SectionCard>
    );
  }

  return (
    <SectionCard
      title={t('klara.brief.researchTitle')}
      subtitle={t('klara.brief.researchHint')}
    >
      <Box sx={{ display: 'flex', gap: '20px', alignItems: 'stretch' }}>
        <ChoiceCard
          title={t('klara.brief.researchOn')}
          body={t('klara.brief.researchOnBody')}
          selected={value}
          onSelect={() => onChange(true)}
        />
        <ChoiceCard
          title={t('klara.brief.researchOff')}
          body={t('klara.brief.researchOffBody')}
          selected={!value}
          onSelect={() => onChange(false)}
        />
      </Box>
    </SectionCard>
  );
}


